import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const runtimeReport = z.object({
  message: z.string().min(1).max(2000),
  stack: z.string().max(20000).optional(),
  source: z.string().max(500).optional(),
  severity: z.enum(["warning", "error", "fatal"]).default("error"),
  correlationId: z.string().max(120).optional(),
  buildId: z.string().max(120).optional(),
  releaseChannel: z.string().max(40).optional(),
  actorRole: z.enum(["student", "teacher", "parent"]).optional(),
  context: z.record(z.unknown()).optional(),
});

/** Registra un errore di runtime del client nel registro interno. */
export const reportRuntimeError = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => runtimeReport.parse(data))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("error_reports").insert({
      kind: "runtime",
      severity: data.severity,
      message: data.message.slice(0, 2000),
      stack: data.stack ?? null,
      source: data.source ?? null,
      user_id: context.userId,
      correlation_id: data.correlationId ?? null,
      build_id: data.buildId ?? null,
      release_channel: data.releaseChannel ?? null,
      actor_role: data.actorRole ?? null,
      context: JSON.parse(JSON.stringify(data.context ?? {})),
    });
    if (error) throw error;
    return { ok: true };
  });

const listInput = z.object({
  kind: z.enum(["all", "build", "runtime"]).default("all"),
  status: z.enum(["all", "new", "in_progress", "resolved"]).default("all"),
  role: z.enum(["all", "student", "teacher", "parent"]).default("all"),
  branch: z.string().trim().max(200).optional(),
  commit: z.string().trim().max(80).optional(),
  correlationId: z.string().trim().max(120).optional(),
  search: z.string().trim().max(200).optional(),
  limit: z.number().int().min(1).max(200).default(50),
});

/** Elenco filtrabile degli errori per la dashboard interna (solo amministratori). */
export const listErrorReports = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => listInput.parse(data ?? {}))
  .handler(async ({ data, context }) => {
    const { data: admin } = await context.supabase.rpc("is_internal_admin", {
      _user_id: context.userId,
    });
    if (!admin) return { admin: false as const, reports: [], branches: [] as string[] };

    let query = context.supabase
      .from("error_reports")
      .select(
        "id,kind,severity,message,stack,source,commit_sha,branch,run_url,context,created_at,triage_status,triage_note,triaged_at,correlation_id,actor_role,build_id,release_channel",
      )
      .order("created_at", { ascending: false })
      .limit(data.limit);

    if (data.kind !== "all") query = query.eq("kind", data.kind);
    if (data.status !== "all") query = query.eq("triage_status", data.status);
    if (data.role !== "all") query = query.eq("actor_role", data.role);
    if (data.branch) query = query.eq("branch", data.branch);
    if (data.commit) query = query.ilike("commit_sha", `${data.commit}%`);
    if (data.correlationId) query = query.eq("correlation_id", data.correlationId);
    if (data.search) {
      const term = data.search.replace(/[%,]/g, " ");
      query = query.or(`message.ilike.%${term}%,source.ilike.%${term}%`);
    }

    const { data: reports, error } = await query;
    if (error) throw error;

    const { data: branchRows } = await context.supabase
      .from("error_reports")
      .select("branch")
      .not("branch", "is", null)
      .order("created_at", { ascending: false })
      .limit(200);
    const branches = [...new Set((branchRows ?? []).map((r) => r.branch as string))].filter(Boolean);

    return { admin: true as const, reports: reports ?? [], branches };
  });

const triageInput = z.object({
  id: z.string().uuid(),
  status: z.enum(["new", "in_progress", "resolved"]),
  note: z.string().trim().max(1000).optional(),
});

/** Aggiorna lo stato di triage di una segnalazione (solo amministratori). */
export const updateReportTriage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => triageInput.parse(data))
  .handler(async ({ data, context }) => {
    const { data: admin } = await context.supabase.rpc("is_internal_admin", {
      _user_id: context.userId,
    });
    if (!admin) throw new Error("Forbidden");

    const { error } = await context.supabase
      .from("error_reports")
      .update({
        triage_status: data.status,
        triage_note: data.note ?? null,
        triaged_by: context.userId,
        triaged_at: new Date().toISOString(),
      })
      .eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

const symbolicateInput = z.object({ id: z.string().uuid() });

/** Traduce lo stack di produzione in file e righe originali usando le source map. */
export const symbolicateReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => symbolicateInput.parse(data))
  .handler(async ({ data, context }) => {
    const { data: admin } = await context.supabase.rpc("is_internal_admin", {
      _user_id: context.userId,
    });
    if (!admin) throw new Error("Forbidden");

    const { data: row, error } = await context.supabase
      .from("error_reports")
      .select("stack,build_id")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw error;
    if (!row?.stack) return { frames: [], reason: "no-stack" as const };
    if (!row.build_id) return { frames: [], reason: "no-build-id" as const };

    const { symbolicate } = await import("@/lib/sourcemaps.server");
    const frames = await symbolicate(row.stack, row.build_id);
    const mapped = frames.some((f) => f.original);
    return { frames, reason: mapped ? ("ok" as const) : ("no-sourcemap" as const) };
  });
